// src/pages/InvoicesPage.js
import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Download, 
  CreditCard, 
  AlertCircle, 
  Filter, 
  CheckCircle, 
  Clock, 
  AlertTriangle,
  ChevronDown,
  Trash2
} from 'lucide-react';
import { invoicesAPI } from '../services/api';
import { Link, useNavigate } from 'react-router-dom';

const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();

  // Status-Definitionen mit Farben und Icons
  const STATUS_CONFIG = {
    pending: { 
      label: 'Ausstehend', 
      color: 'bg-yellow-100 text-yellow-800', 
      icon: <Clock className="h-4 w-4 text-yellow-600" /> 
    },
    paid: { 
      label: 'Bezahlt', 
      color: 'bg-green-100 text-green-800', 
      icon: <CheckCircle className="h-4 w-4 text-green-600" /> 
    },
    overdue: { 
      label: 'Überfällig', 
      color: 'bg-red-100 text-red-800', 
      icon: <AlertTriangle className="h-4 w-4 text-red-600" /> 
    }
  };

  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      try {
        const response = await invoicesAPI.getAll();
        setInvoices(response.data.invoices);
        setFilteredInvoices(response.data.invoices);
        setError('');
      } catch (err) {
        console.error('Fehler beim Laden der Rechnungen:', err);
        setError('Fehler beim Laden der Rechnungen');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  useEffect(() => {
    filterInvoices();
  }, [statusFilter, invoices]);

  const filterInvoices = () => {
    if (statusFilter === 'all') {
      setFilteredInvoices(invoices);
    } else {
      const filtered = invoices.filter(invoice => invoice.status === statusFilter);
      setFilteredInvoices(filtered);
    }
  };

  const downloadPDF = async (id) => {
    try {
      // Statt Blob-Ansatz direkt die API-URL öffnen
      const token = localStorage.getItem('token');
      const timestamp = new Date().getTime(); // Zeitstempel hinzufügen, um den Cache zu umgehen
      const apiUrl = `${process.env.REACT_APP_API_URL || '/api'}/invoices/${id}/pdf`;
      
      // URL mit Token als Query-Parameter für die Authentifizierung und Cache-Busting
      const pdfUrl = `${apiUrl}?token=${token}&t=${timestamp}`;
      
      // PDF in einem neuen Tab öffnen
      window.open(pdfUrl, '_blank');
    } catch (err) {
      console.error('Fehler beim Öffnen der Rechnung:', err);
      setError('Fehler beim Öffnen der Rechnung');
    }
  };

  const markAsPaid = async (id) => {
    if (!window.confirm('Möchten Sie diese Rechnung als bezahlt markieren?')) return;
    
    try {
      const now = new Date();
      const mysqlDate = now.toISOString().slice(0, 19).replace('T', ' ');
      
      await invoicesAPI.markAsPaid(id, { paid_at: mysqlDate });
      
      const response = await invoicesAPI.getAll();
      setInvoices(response.data.invoices);
      filterInvoices();
      
      setError('Rechnung erfolgreich als bezahlt markiert');
    } catch (err) {
      console.error('Fehler beim Markieren der Rechnung als bezahlt:', err);
      setError('Fehler beim Markieren der Rechnung als bezahlt');
    }
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm('Möchten Sie diese Rechnung wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) return;
    
    try {
      await invoicesAPI.delete(id);
      
      // Aktualisierte Liste abrufen
      const response = await invoicesAPI.getAll();
      setInvoices(response.data.invoices);
      filterInvoices();
      
      setError('Rechnung erfolgreich gelöscht');
    } catch (err) {
      console.error('Fehler beim Löschen der Rechnung:', err);
      setError('Fehler beim Löschen der Rechnung');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  const formatAmount = (amount) => {
    return 'CHF ' + parseFloat(amount).toFixed(2);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <FileText className="h-10 w-10 sm:h-12 sm:w-12 text-primary-600 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen p-4 sm:p-8">
      <div className="container mx-auto">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-8 gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center">
            <CreditCard className="mr-2 sm:mr-4 h-6 w-6 sm:h-8 sm:w-8 text-primary-600" />
            Rechnungen
          </h1>
          <div className="flex items-center gap-4">
            <div className="relative w-full sm:w-auto">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full sm:w-auto pl-8 pr-4 py-2 border border-gray-300 rounded appearance-none"
              >
                <option value="all">Alle Status</option>
                {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                  <option key={status} value={status}>
                    {config.label}
                  </option>
                ))}
              </select>
              <Filter className="absolute left-2 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
            </div>
            <button
              onClick={() => navigate('/rechnungen/neu/manuell')}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded flex items-center gap-2"
            >
              <CreditCard size={16} />
              <span>Manuelle Rechnung</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-3 sm:p-4 mb-4 sm:mb-6 flex items-center">
            <AlertCircle className="mr-2 sm:mr-3 h-5 w-5 text-red-500" />
            <p className="text-xs sm:text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-4 sm:mb-6">
          <div className="flex justify-between items-center mb-3 sm:mb-4">
            <h2 className="text-lg sm:text-xl font-medium flex items-center">
              <FileText className="mr-2 sm:mr-3 h-5 w-5 sm:h-6 sm:w-6 text-primary-600" />
              Rechnungsübersicht
            </h2>
          </div>
          
          {filteredInvoices.length === 0 ? (
            <div className="text-center py-6 sm:py-8 text-gray-500">
              <CreditCard className="mx-auto h-10 w-10 sm:h-12 sm:w-12 mb-3 sm:mb-4 text-gray-300" />
              <p className="text-sm sm:text-base">
                {statusFilter === 'all' 
                  ? 'Keine Rechnungen vorhanden' 
                  : `Keine Rechnungen mit Status "${STATUS_CONFIG[statusFilter].label}" gefunden`}
              </p>
            </div>
          ) : (
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {filteredInvoices.map((invoice) => {
                const statusConfig = STATUS_CONFIG[invoice.status] || {};
                return (
                  <div 
                    key={invoice.id} 
                    className="bg-white border rounded-lg shadow-md p-3 sm:p-4 hover:shadow-lg transition"
                  >
                    <div className="flex justify-between items-start mb-3 sm:mb-4">
                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                          Rechnung #{invoice.id}
                          {invoice.reminder_level && (
                            <span className="ml-2 text-red-600 text-sm">
                              (Mahnung {invoice.reminder_level})
                            </span>
                          )}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-500">
                          {invoice.customer_name}
                        </p>
                      </div>
                      <span 
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}
                      >
                        {statusConfig.icon}
                        <span className="ml-1">{statusConfig.label}</span>
                      </span>
                    </div>
                    <div className="mb-3 sm:mb-4">
                      <p className="text-xl sm:text-2xl font-bold text-gray-800">
                        {formatAmount(invoice.total)}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-500">
                        Datum: {formatDate(invoice.created_at)}
                      </p>
                      {invoice.status !== 'paid' && (
                        <p className="text-xs sm:text-sm text-gray-500">
                          Fällig bis: {formatDate(invoice.due_date)}
                        </p>
                      )}
                    </div>
                    <div className="flex justify-between items-center border-t pt-2 sm:pt-3">
                      <div className="text-xs sm:text-sm text-gray-500">
                        {invoice.payment_type === 'cash' ? 'Bar' : 'Überweisung'}
                      </div>
                      <div className="flex space-x-2">
                        <Link 
                          to={`/rechnungen/${invoice.id}`}
                          className="text-primary-600 hover:text-primary-800"
                          title="Details"
                        >
                          <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
                        </Link>
                        <button 
                          onClick={() => downloadPDF(invoice.id)}
                          className="text-primary-600 hover:text-primary-800"
                          title="PDF herunterladen"
                        >
                          <Download className="h-4 w-4 sm:h-5 sm:w-5" />
                        </button>
                        {invoice.status === 'pending' && (
                          <button 
                            onClick={() => markAsPaid(invoice.id)}
                            className="text-green-600 hover:text-green-800"
                            title="Als bezahlt markieren"
                          >
                            <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                          </button>
                        )}
                        <button 
                          onClick={() => deleteInvoice(invoice.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Rechnung löschen"
                        >
                          <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Statistiken */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {Object.entries(STATUS_CONFIG).map(([status, config]) => {
            const filteredByStatus = invoices.filter(invoice => invoice.status === status);
            return (
              <div 
                key={status} 
                className="bg-white p-3 sm:p-4 rounded-lg shadow-md hover:shadow-lg transition"
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-base sm:text-lg font-medium flex items-center">
                    {config.icon}
                    <span className="ml-2">{config.label} Rechnungen</span>
                  </h3>
                </div>
                <p className={`text-xl sm:text-2xl font-bold ${config.color.split(' ')[1]}`}>
                  {filteredByStatus.length}
                </p>
                <p className="text-xs sm:text-sm text-gray-500">
                  Gesamtbetrag: CHF {filteredByStatus
                    .reduce((sum, invoice) => sum + parseFloat(invoice.total), 0)
                    .toFixed(2)}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default InvoicesPage;
